import React, { ComponentProps, ReactNode } from 'react';

import { PublicPageLayout } from '@adsk/alloy-project-page-layout';
import { Box, Flex } from '@adsk/alloy-react-box';
import { LinkButton } from '@adsk/alloy-react-button';
import EmptyState from '@adsk/alloy-react-empty-state';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';
import { StylableComponent } from '@adsk/alloy-react-theme';

import { useTranslation } from './i18n';

export interface DefaultActionProps {
  onRetry?: () => void;
}

export const DefaultAction = ({
  onRetry = () => window.location.reload(),
}: DefaultActionProps) => {
  const { t } = useTranslation();
  return <LinkButton onClick={() => onRetry?.()}>{t('TRY_AGAIN')}</LinkButton>;
};

type EmptyStateProps = ComponentProps<typeof EmptyState>;

type ErrorPageProps = Omit<StylableComponent<HTMLDivElement>, 'title'> & {
  illustrationType?: EmptyStateProps['illustrationType'];
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  footer?: ReactNode;
  onLogout?: () => void;
};

/**
 * An ErrorPage component. Used to show a generic error page when no other option exists.
 * Can be used as a fallback when using a page level ErrorBoundary for example.
 */
const ErrorPage = React.forwardRef<HTMLDivElement, ErrorPageProps>(
  (
    {
      illustrationType = EmptyState.ILLUSTRATION_TYPES
        .BUILDING_CONSTRUCTION_GREY,
      title,
      description,
      action,
      footer,
      onLogout,
      color: _color, // this prop is ignored to fix an error with Box getting wrong color type
      style,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const finalTitle = title || t('ERROR_PAGE_TITLE');
    const finalDescription = description || t('ERROR_PAGE_DESCRIPTION');
    const finalAction = action || <DefaultAction />;

    return (
      <PublicPageLayout onLogout={onLogout} ref={ref}>
        <Flex
          alignItems="center"
          justifyContent="center"
          height="100%"
          style={style}
          {...props}
        >
          <EmptyState
            title={finalTitle}
            illustrationType={illustrationType}
            description={finalDescription}
            size={EmptyState.SIZES.LARGE}
          >
            <>
              {finalAction && <Box typography="heading2">{finalAction}</Box>}
              {footer && (
                <Box
                  marginTop={72}
                  typography="bodyLargeBold"
                  color="charcoal700"
                >
                  {footer}
                </Box>
              )}
            </>
          </EmptyState>
        </Flex>
      </PublicPageLayout>
    );
  },
);

ErrorPage.displayName = 'ErrorPage';

export default Object.assign(WithAlloySuspense(ErrorPage), {
  ILLUSTRATION_TYPES: EmptyState.ILLUSTRATION_TYPES,
});
